import React, { ReactNode } from 'react';

import { NOTIFICATION_STATUSES, NotificationStatus } from './consts';

type AsyncResult = {
  status?: NotificationStatus;
  message?: string;
  footer?: ReactNode;
};

type UseAsyncNotificationArgs = {
  asyncOperation?: Promise<unknown>;
  status: NotificationStatus;
  footer?: ReactNode;
};

type UseAsyncNotificationResult = {
  isLoading: boolean;
  finalStatus: NotificationStatus;
  asyncDismissible: boolean;
  activeFooter: ReactNode;
  message?: string;
};

/**
 * Resolves the optional `asyncOperation` promise and derives the toast's
 * effective loading, status, dismissibility, footer, and message state.
 *
 * The `cancelled` flag guards `.then`/`.catch` callbacks on unmount so React 17
 * consumers don't receive a post-unmount `setState` warning. On React 18+ the
 * flag is a no-op (the runtime silently ignores state updates on unmounted
 * fibers), but it is kept intentionally so the guard remains in place if the
 * library is ever used against React 17.
 */
export default function useAsyncNotification({
  asyncOperation,
  status,
  footer,
}: UseAsyncNotificationArgs): UseAsyncNotificationResult {
  const hasPromise =
    !!asyncOperation && typeof asyncOperation.then === 'function';
  const [asyncResult, setAsyncResult] = React.useState<AsyncResult>();

  React.useEffect(() => {
    let cancelled = false;

    if (hasPromise) {
      asyncOperation
        ?.then((result) => {
          if (!cancelled) setAsyncResult(result as AsyncResult);
        })
        .catch((error: unknown) => {
          if (!cancelled) setAsyncResult(error as AsyncResult);
        });
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeFooter =
    asyncResult && 'footer' in asyncResult ? asyncResult.footer : footer;

  return {
    isLoading: hasPromise && !asyncResult,
    finalStatus: asyncResult?.status ?? status ?? NOTIFICATION_STATUSES.INFO,
    asyncDismissible: hasPromise && !!asyncResult,
    activeFooter,
    message: asyncResult?.message,
  };
}
